import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { features } from '@ui/components/Features';
import { TokenIcon } from '@ui/components/TokenIcon';
import type { TokenProtocol } from '@ui/components/useYourTokenDrawer';
import useGardenApy from '@ui/hooks/useGardenApy';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTectonicSupplyUsd } from '@ui/hooks/usePartnerStats';
import { useSingleFinanceLcroFarmInfo } from '@ui/hooks/useSingleFinanceLcroFarmInfo';
import { useTranslations } from '@ui/i18n';
import { getFerroTotalLockedValue } from '@ui/queries/ferroTotalLockedValue';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { formatNumber, formatPercentage } from '@ui/utils/format';
import { formatGardenApy, getGardenName } from '@ui/utils/garden';

export const useLcroProtocols = (): TokenProtocol[] => {
  const t = useTranslations();

  const tectonicTvlRet = useTectonicSupplyUsd();
  const singleFinanceFarmTvlRet = useSingleFinanceLcroFarmInfo();
  const singleFinanceLendTvlRet = useSingleFinanceLendInfo();

  // same as in useUseLcroPartnerCardsData
  const {
    ferroGardenApy: ferroApy,
    tectonicGardenApy: tectonicApy,
    vvsCroGardenApy: vvsApy,
  } = useGardenApy(undefined, true);

  const ferroTvlRet = useQuery({
    ...getFerroTotalLockedValue({
      coinMode: 'cro',
    }),
  });

  const lcroProtocols: TokenProtocol[] = [];

  const list: (TokenProtocol | null)[] = [
    {
      logo: '/images/use-ferro.png',
      title: 'Ferro',
      subtitle: t('Provide Liquidity'),
      titleTail: ferroApy ? `${formatGardenApy(ferroApy)}` : undefined,
      tvl: ferroTvlRet.data
        ? `$${formatNumber(ferroTvlRet.data, 2)}`
        : undefined,
      description: t(
        "Provide liquidity for {liquidCoin} on Cronos' leading stable swap and earn additional rewards on your {liquidCoin} Earn even more with the {garden}",
        {
          liquidCoin: 'LCRO',
          garden: getGardenName('cro', 'FERRO'),
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://ferroprotocol.com/',
    },
    {
      logo: '/images/use-tectonic.png',
      title: 'Tectonic',
      titleTail: tectonicApy ? `${formatGardenApy(tectonicApy)}` : undefined,
      tvl: tectonicTvlRet.data
        ? `$${formatNumber(bigNumberToNumber(tectonicTvlRet.data), 2)}`
        : undefined,
      subtitle: t('Lend {coin}', {
        coin: 'LCRO',
      }),
      description: t(
        'Supply {liquidCoin} on the leading money market on Cronos and earn interest from borrowers Earn additional rewards with the {garden}',
        {
          liquidCoin: 'LCRO',
          garden: getGardenName('cro', 'TECTONIC'),
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://tectonic.finance/',
    },
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      subtitle: t('Leveraged Yield Farming'),
      description: t(
        'Use {liquidCoin} for leveraged yield farming on Single Finance',
        {
          liquidCoin: 'LCRO',
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/farm',
      titleTail: singleFinanceFarmTvlRet.data?.apy
        ? formatPercentage(singleFinanceFarmTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceFarmTvlRet.data?.tvlInUSD
        ? `$${formatNumber(singleFinanceFarmTvlRet.data.tvlInUSD, 2)}`
        : undefined,
    },
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      subtitle: t('Lend {coin}', {
        coin: 'LCRO',
      }),
      description: t(
        'Supply {liquidCoin} to leveraged yield farmers and earn interest',
        {
          liquidCoin: 'LCRO',
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/lend',
      titleTail: singleFinanceLendTvlRet.data?.apy
        ? formatPercentage(singleFinanceLendTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceLendTvlRet.data?.tvlInUSD
        ? `$${formatNumber(singleFinanceLendTvlRet.data.tvlInUSD, 2)}`
        : undefined,
    },
    {
      logo: '/images/use-vvs.png',
      title: 'VVS Finance',
      titleTail: vvsApy ? `${formatGardenApy(vvsApy)}` : undefined,
      tvl: undefined,
      subtitle: 'Farm',
      description: t(
        'Put your {liquidCoin} to work on VVS Finance to earn attractive yield farming rewards!',
        {
          liquidCoin: 'LCRO',
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://vvs.finance/',
    },
    isFeatureEnabled(features.wowmaxxSupport)
      ? {
          logo: '/images/wowmax.svg',
          title: 'Wowmaxx',
          subtitle: t('Trade {coin}', {
            coin: 'LCRO',
          }),
          description: t(
            'Trade large amounts of {liquidCoin} with optimized slippage',
            {
              liquidCoin: 'LCRO',
            },
          ),
          tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
          url: 'https://app.wowmax.exchange/swap/25/LCRO/CRO',
        }
      : null,
  ];

  for (const item of list) {
    if (item) {
      lcroProtocols.push(item);
    }
  }

  return lcroProtocols;
};

const useSingleFinanceLendInfo = (
  options: UseQueryOptions<{
    tvlInUSD: number | undefined;
    apy: number | undefined;
  }> = {},
) => {
  return useQuery<{
    tvlInUSD: number | undefined;
    apy: number | undefined;
  }>(
    ['useSingleFinanceLendInfo'],
    async () => {
      const fetchResponse = await fetch(
        'https://api.singlefinance.io/api/info/vaults?chainid=25',
      );
      const response = (await fetchResponse.json()) as {
        data: {
          vaults: {
            name: string;
            tvlInUSD?: number | undefined;
            apy?: number | undefined;
          }[];
        };
      };

      const tokenInfo = response.data.vaults.find((v) => v.name === 'ibLCRO');

      return {
        tvlInUSD: tokenInfo?.tvlInUSD,
        apy: tokenInfo?.apy,
      };
    },
    {
      ...options,
    },
  );
};
